import React, { useMemo } from 'react'
import Alert from '../../atoms/Alert/Alert'
import AlertGroup from '../../atoms/AlertGroup/AlertGroup'
import useTrans from '../../../hooks/useTrans'

export const CART_ALERT_ERROR_TYPES = Object.freeze({
  NON_PRENOTABILE: 'non_prenotabile',
  NON_DISPONIBILE: 'non_disponibile',
  NO_MULTIPLI: 'no_multipli',
  NO_MULTIPLI_BLOCCANTE: 'no_multipli_bloccante',
  QUANTITA_MINIMA_PERSONALIZZABILE: 'quantita_minima_personalizzabile',
  NO_LOGO_PANTONI: 'no_logo_pantoni',
  NO_FORNITORE: 'no_fornitore',
  NO_SPEDIZIONE_GRATIS: 'no_spedizione_gratis',
  CONTIENE_PRENOTAZIONE: 'contiene_prenotazione',
})

export const ALERT_GROUP_ERROR_TYPES = [
  CART_ALERT_ERROR_TYPES.NO_MULTIPLI,
  CART_ALERT_ERROR_TYPES.NO_MULTIPLI_BLOCCANTE,
]

export const getPlaceholderData = (errore, t, cartMain) => {
  switch (errore.tipo) {
    case CART_ALERT_ERROR_TYPES.NON_PRENOTABILE:
      return {
        placeholder: {
          single: t(
            "La disponibilità dell'articolo {0} è variata e non soddisfa la quantità inserita.",
            [`<strong>${errore.riga.articolo.codice}</strong>`]
          ),
          multiple: t(
            'La disponibilità dei seguenti articoli è variata e non soddisfano le quantità inserita.'
          ),
        },
        alert: errore.bloccante,
      }
    case CART_ALERT_ERROR_TYPES.NON_DISPONIBILE:
      return {
        placeholder: {
          single: t(" La disponibilità dell'articolo {0} è variata.", [
            `<strong>${errore.riga.articolo.codice}</strong>`,
          ]),
          multiple: t(' La disponibilità dei seguenti articoli è variata.'),
        },
        alert: errore.bloccante,
      }
    case CART_ALERT_ERROR_TYPES.NO_MULTIPLI:
      return {
        placeholder: {
          single: t(
            "L'articolo {0} contiene sfusi. Ordina per imballi e/o sotto imballi per ottenere uno sconto del 2%.",
            [`<strong>${errore.riga.articolo.codice}</strong>`]
          ),
          multiple: t(
            'Ci sono articoli che contengono sfusi. Ordina per imballi e/o sotto imballi per ottenere uno sconto del 2%.'
          ),
        },
        alert: errore.bloccante,
      }
    case CART_ALERT_ERROR_TYPES.NO_MULTIPLI_BLOCCANTE:
      return {
        placeholder: {
          single: t("L'articolo {0} è acquistabile solo a multipli di {1} pezzi.", [
            `<strong>${errore.riga.articolo.codice}</strong>`,
            errore?.quantita_minima_personalizzabile,
          ]),
        },
        alert: errore.bloccante,
      }
    case CART_ALERT_ERROR_TYPES.QUANTITA_MINIMA_PERSONALIZZABILE:
      return {
        placeholder: {
          single: t(
            ' ' +
              `nella lavorazione {0} non viene raggiunta la quantità minima personalizzabile di {1} pezzi.` +
              ' ',
            [
              `<strong>${errore?.nome_lavorazione}</strong>`,
              errore?.quantita_minima_personalizzabile,
            ]
          ),
        },
        alert: errore.bloccante,
        noClick: true,
      }
    case CART_ALERT_ERROR_TYPES.NO_LOGO_PANTONI:
      return {
        placeholder: {
          single: t(
            ' ' + `nella lavorazione {0} non sono stati caricati i loghi e/o i pantoni` + ' ',
            [
              `<strong>${
                cartMain?.personalizzazioni_carrello?.filter(
                  (personalizzazione) => personalizzazione.id === errore.personalizzazione_id
                )[0]?.nome_lavorazione
              }</strong>`,
            ]
          ),
        },
        alert: errore.bloccante,
      }
    case CART_ALERT_ERROR_TYPES.NO_FORNITORE:
      return {
        placeholder: {
          single: t(
            ' ' +
              `nella lavorazione {0} non è possibile ordinare la quantità scelta. Contattare l'ufficio commerciale` +
              ' ',
            [
              cartMain?.personalizzazioni_carrello?.filter(
                (personalizzazione) => personalizzazione.id === errore.personalizzazione_id
              )[0]?.nome_lavorazione,
            ]
          ),
        },
        link: null,
        alert: errore.bloccante,
      }
    case CART_ALERT_ERROR_TYPES.NO_SPEDIZIONE_GRATIS:
      return {
        placeholder: {
          single: t(
            `Risparmia il costo della spedizione ordinando almeno {0} Euro di merce a imballi e sotto imballi originali!`,
            [errore?.importo_spedizione_gratuita]
          ),
        },
        alert: errore.bloccante,
        noClick: true,
      }
    case CART_ALERT_ERROR_TYPES.CONTIENE_PRENOTAZIONE:
      return {
        placeholder: {
          single: t(`L'ordine contiene merce prenotata`),
        },
        alert: errore.bloccante,
      }
    default:
      return {
        placeholder: {
          single: errore?.messaggio || '',
        },
        alert: errore?.bloccante,
      }
  }
}

const ErrorAlertsList = ({
  errori,
  cartMain,
  onAlertClick,
  alertGroupTypes = ALERT_GROUP_ERROR_TYPES,
  filterTypes,
}) => {
  const t = useTrans()

  const erroriFiltrati = useMemo(() => {
    if (!Array.isArray(errori)) return []
    if (!filterTypes || filterTypes.length === 0) return errori
    return errori.filter((err) => filterTypes.includes(err?.tipo))
  }, [errori, filterTypes])

  const erroriRaggruppati = useMemo(() => {
    if (!Array.isArray(erroriFiltrati)) {
      return []
    }

    const groupedEntries = []
    const groupedMap = {}

    erroriFiltrati.forEach((errore) => {
      const erroreTipo = errore?.tipo

      if (alertGroupTypes.includes(erroreTipo)) {
        if (!groupedMap[erroreTipo]) {
          groupedMap[erroreTipo] = {
            tipo: erroreTipo,
            errori: [errore],
            grouped: true,
          }
          groupedEntries.push(groupedMap[erroreTipo])
        } else {
          groupedMap[erroreTipo].errori.push(errore)
        }
      } else {
        groupedEntries.push({
          tipo: erroreTipo,
          errore,
          grouped: false,
        })
      }
    })

    return groupedEntries
  }, [alertGroupTypes, erroriFiltrati])

  return (
    <div>
      {erroriRaggruppati.map((item, index) => {
        if (item.grouped && item.errori.length > 1) {
          const placeholderData = getPlaceholderData(item.errori?.[0], t, cartMain)
          const multiPlaceholder =
            typeof placeholderData?.placeholder === 'object'
              ? placeholderData?.placeholder?.multiple ||
                placeholderData?.placeholder?.single
              : placeholderData?.placeholder
          return (
            <div style={{ marginBottom: 15 }} key={`cart-alert-group-${item.tipo || index}`}>
              <AlertGroup
                tipo={item.tipo}
                errori={item.errori}
                placeholder={multiPlaceholder}
                variant={!placeholderData?.alert && 'info'}
                onChipClick={onAlertClick}
              />
            </div>
          )
        }

        const errore = item.grouped ? item.errori[0] : item.errore
        const placeholderData = getPlaceholderData(errore, t, cartMain)
        const singlePlaceholder =
          typeof placeholderData?.placeholder === 'object'
            ? placeholderData?.placeholder?.single
            : placeholderData?.placeholder

        return (
          <div style={{ marginBottom: 15 }} key={`cart-alert-${errore?.riga?.id || index}`}>
            <Alert
              full
              noClick={placeholderData?.noClick}
              variant={!placeholderData?.alert && 'info'}
              onClick={
                placeholderData?.noClick || !onAlertClick ? undefined : () => onAlertClick(errore)
              }
              placeholder={singlePlaceholder}
            />
          </div>
        )
      })}
    </div>
  )
}

export default ErrorAlertsList
